import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import Page from "./Page";
import Card from "../layout/Card";

import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const EMPTY_ROW = {
  entityName: "",
  filterMode: "builder",
  combinator: "and",
  conditions: [],
  filterJson: "{}",
  presetName: "",
  selectedPresetId: "",
};

const PRESET_STORAGE_KEY = "artifactBuilder.filterPresets.v1";
const MAX_INLINE_ARTIFACT_BYTES = 12 * 1024 * 1024;

const OPERATOR_OPTIONS = [
  { value: "equals", label: "Equals" },
  { value: "notEquals", label: "Not Equal" },
  { value: "contains", label: "Contains" },
  { value: "in", label: "In (comma-separated)" },
  { value: "gt", label: "Greater Than" },
  { value: "gte", label: "Greater or Equal" },
  { value: "lt", label: "Less Than" },
  { value: "lte", label: "Less or Equal" },
  { value: "exists", label: "Exists" },
];

const VALUE_TYPE_OPTIONS = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "date", label: "Date (ISO Text)" },
  { value: "objectId", label: "ObjectId (Text)" },
];

const ENTITY_OPTIONS = [
  "Banner",
  "Call",
  "CallResult",
  "Client",
  "Location",
  "Order",
  "Project",
  "ProjectLocation",
  "Retailer",
  "SKU",
  "SKUListing",
  "SKUPricing",
  "Survey",
  "SurveyAnswer",
  "SurveyQuestion",
  "SurveySection",
  "User",
];

const escapeRegex = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const coerceFilterValue = (value, valueType) => {
  if (valueType === "number") {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : value;
  }

  if (valueType === "boolean") {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();

    if (["true", "1", "yes"].includes(normalized)) return true;
    if (["false", "0", "no"].includes(normalized)) return false;
    return value;
  }

  return value;
};

const buildConditionExpression = (condition) => {
  const field = String(condition?.field || "").trim();
  if (!field) return null;
  const resolvedField = field === "id" ? "_id" : field;

  const operator = String(condition?.operator || "equals");
  const rawValue = condition?.value;
  const valueType = String(condition?.valueType || "string");

  if (operator === "exists") {
    const normalized = String(rawValue || "")
      .trim()
      .toLowerCase();
    const exists = !["false", "0", "no"].includes(normalized);
    return { [resolvedField]: { $exists: exists } };
  }

  if (
    rawValue === undefined ||
    rawValue === null ||
    String(rawValue).trim() === ""
  ) {
    return null;
  }

  const coercedValue = coerceFilterValue(rawValue, valueType);

  if (operator === "equals") return { [resolvedField]: coercedValue };
  if (operator === "notEquals")
    return { [resolvedField]: { $ne: coercedValue } };
  if (operator === "contains") {
    return {
      [resolvedField]: {
        $regex: escapeRegex(coercedValue),
        $options: "i",
      },
    };
  }

  if (operator === "in") {
    const values = String(rawValue)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => coerceFilterValue(item, valueType));

    return values.length > 0 ? { [resolvedField]: { $in: values } } : null;
  }

  if (operator === "gt") return { [resolvedField]: { $gt: coercedValue } };
  if (operator === "gte") return { [resolvedField]: { $gte: coercedValue } };
  if (operator === "lt") return { [resolvedField]: { $lt: coercedValue } };
  if (operator === "lte") return { [resolvedField]: { $lte: coercedValue } };

  return { [resolvedField]: coercedValue };
};

const buildFilterFromConditions = (row) => {
  const conditions = Array.isArray(row?.conditions) ? row.conditions : [];
  const expressions = conditions
    .map((condition) => buildConditionExpression(condition))
    .filter(Boolean);

  if (expressions.length === 0) {
    return {};
  }

  if (expressions.length === 1) {
    return expressions[0];
  }

  if (row?.combinator === "or") {
    return { $or: expressions };
  }

  return { $and: expressions };
};

const normalizeBooleanText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const validateConditionValue = (condition) => {
  const fieldPath = String(condition?.field || "").trim();
  const rawValueText = String(condition?.value ?? "").trim();

  // Completely blank condition rows are treated as "no condition".
  if (!fieldPath && !rawValueText) {
    return null;
  }

  if (!fieldPath) {
    return "Field path is required.";
  }

  const operator = String(condition?.operator || "equals");
  const valueType = String(condition?.valueType || "string");
  const rawValue = String(condition?.value ?? "").trim();

  if (operator === "exists") {
    if (!rawValue) return null;

    const normalized = normalizeBooleanText(rawValue);
    if (["true", "false", "1", "0", "yes", "no"].includes(normalized)) {
      return null;
    }

    return "Exists expects true/false.";
  }

  if (!rawValue) {
    return "Value is required for this operator.";
  }

  if (operator === "in") {
    const parts = rawValue
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    if (parts.length === 0) {
      return "In requires one or more comma-separated values.";
    }

    if (valueType === "number") {
      const allNumeric = parts.every((part) => Number.isFinite(Number(part)));
      if (!allNumeric) {
        return "All In values must be valid numbers.";
      }
    }

    if (valueType === "boolean") {
      const allBooleanText = parts.every((part) =>
        ["true", "false", "1", "0", "yes", "no"].includes(
          normalizeBooleanText(part),
        ),
      );

      if (!allBooleanText) {
        return "All In values must be true/false.";
      }
    }

    return null;
  }

  if (valueType === "number") {
    if (!Number.isFinite(Number(rawValue))) {
      return "Value must be a valid number.";
    }
  }

  if (valueType === "boolean") {
    if (
      !["true", "false", "1", "0", "yes", "no"].includes(
        normalizeBooleanText(rawValue),
      )
    ) {
      return "Value must be true/false.";
    }
  }

  if (valueType === "date") {
    if (!Number.isFinite(Date.parse(rawValue))) {
      return "Date must be valid ISO text (e.g., 2026-04-06).";
    }
  }

  return null;
};

const conditionHint = (condition) => {
  const operator = String(condition?.operator || "equals");
  if (operator === "exists") {
    return "Optional value: true/false (defaults to true when blank).";
  }

  if (operator === "in") {
    return "Provide comma-separated values.";
  }

  return "";
};

const ArtifactBuilder = () => {
  const navigate = useNavigate();

  const { addToWorkQueue, executeAction } = useData();
  const { showMessage } = usePage();

  const [artifactName, setArtifactName] = useState("");
  const [description, setDescription] = useState("");
  const [exportMode, setExportMode] = useState("mirror-config");
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [submitting, setSubmitting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importSourceUrl, setImportSourceUrl] = useState("");
  const [importFileName, setImportFileName] = useState("");
  const [importPayload, setImportPayload] = useState(null);
  const [importDryRun, setImportDryRun] = useState(true);
  const [importing, setImporting] = useState(false);
  const [presets, setPresets] = useState([]);
  const [artifactImportPolicy, setArtifactImportPolicy] = useState(null);
  const [artifactPolicyLoaded, setArtifactPolicyLoaded] = useState(false);

  const environment = process.env.REACT_APP_ENVIRONMENT || "unknown";

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!artifactName.trim()) return false;
    if (!rows.length) return false;
    return rows.every((row) => row.entityName.trim().length > 0);
  }, [artifactName, rows, submitting]);

  const canImportArtifact = !!artifactImportPolicy?.canImportArtifact;

  const toolbarItems = useMemo(() => {
    const items = [{ name: "home", iconName: "Home" }];

    if (canImportArtifact) {
      items.push({ name: "import", iconName: "Upload", label: "Import" });
    }

    return items;
  }, [canImportArtifact]);

  useEffect(() => {
    const loadArtifactImportPolicy = async () => {
      const results = await executeAction({
        entityName: "WorkQueue",
        action: "getArtifactImportPolicy",
        data: {},
      });

      if (results?.isSuccessful) {
        setArtifactImportPolicy(results.data || null);
      } else {
        setArtifactImportPolicy({
          canImportArtifact: false,
          reason: "Unable to load artifact import policy.",
        });
      }

      setArtifactPolicyLoaded(true);
    };

    loadArtifactImportPolicy();
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(PRESET_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;

      setPresets(parsed);
    } catch (err) {
      // ignore bad local preset state
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(presets));
    } catch (err) {
      // ignore storage write failures
    }
  }, [presets]);

  const fieldPathOptions = useMemo(() => {
    const values = new Set();

    rows.forEach((row) => {
      (Array.isArray(row.conditions) ? row.conditions : []).forEach(
        (condition) => {
          const path = String(condition?.field || "").trim();
          if (path) values.add(path);
        },
      );
    });

    presets.forEach((preset) => {
      (Array.isArray(preset?.conditions) ? preset.conditions : []).forEach(
        (condition) => {
          const path = String(condition?.field || "").trim();
          if (path) values.add(path);
        },
      );
    });

    return Array.from(values).sort();
  }, [rows, presets]);

  const updateRow = (index, field, value) => {
    setRows((previous) =>
      previous.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  const savePreset = (rowIndex) => {
    const row = rows[rowIndex] || {};
    const presetName = String(row.presetName || "").trim();
    if (!presetName) {
      showMessage(`Preset name is required on row ${rowIndex + 1}.`, "error");
      return;
    }

    const snapshot = {
      id: String(Date.now()),
      name: presetName,
      entityName: String(row.entityName || "").trim(),
      filterMode: row.filterMode || "builder",
      combinator: row.combinator || "and",
      conditions: Array.isArray(row.conditions) ? row.conditions : [],
      filterJson: row.filterJson || "{}",
      updatedAt: new Date().toISOString(),
    };

    setPresets((previous) => {
      const idx = previous.findIndex((preset) => preset.name === presetName);
      if (idx < 0) return [snapshot, ...previous].slice(0, 50);

      const next = [...previous];
      next[idx] = {
        ...next[idx],
        ...snapshot,
        id: next[idx].id || snapshot.id,
      };
      return next;
    });

    showMessage(`Saved preset '${presetName}'.`, "success");
  };

  const applyPreset = (rowIndex) => {
    const selectedId = rows[rowIndex]?.selectedPresetId;
    const preset = presets.find((item) => item.id === selectedId);
    if (!preset) {
      showMessage("Select a preset before applying.", "error");
      return;
    }

    setRows((previous) =>
      previous.map((row, index) => {
        if (index !== rowIndex) return row;

        return {
          ...row,
          entityName: preset.entityName || row.entityName || "",
          filterMode: preset.filterMode || "builder",
          combinator: preset.combinator || "and",
          conditions: Array.isArray(preset.conditions)
            ? preset.conditions
            : row.conditions,
          filterJson: preset.filterJson || row.filterJson || "{}",
          presetName: preset.name || row.presetName,
        };
      }),
    );

    showMessage(`Applied preset '${preset.name}'.`, "success");
  };

  const addRow = () => {
    setRows((previous) => [...previous, { ...EMPTY_ROW }]);
  };

  const addCondition = (rowIndex) => {
    setRows((previous) =>
      previous.map((row, index) =>
        index === rowIndex
          ? {
              ...row,
              conditions: [
                ...(Array.isArray(row.conditions) ? row.conditions : []),
                {
                  field: "",
                  operator: "equals",
                  valueType: "string",
                  value: "",
                },
              ],
            }
          : row,
      ),
    );
  };

  const updateCondition = (rowIndex, conditionIndex, field, value) => {
    setRows((previous) =>
      previous.map((row, index) => {
        if (index !== rowIndex) return row;

        const nextConditions = (
          Array.isArray(row.conditions) ? row.conditions : []
        ).map((condition, index2) =>
          index2 === conditionIndex
            ? {
                ...condition,
                [field]: value,
              }
            : condition,
        );

        const nextRow = {
          ...row,
          conditions: nextConditions,
        };

        if (row.filterMode === "builder") {
          nextRow.filterJson = JSON.stringify(
            buildFilterFromConditions(nextRow),
            null,
            2,
          );
        }

        return nextRow;
      }),
    );
  };

  const removeCondition = (rowIndex, conditionIndex) => {
    setRows((previous) =>
      previous.map((row, index) => {
        if (index !== rowIndex) return row;

        const baseConditions = Array.isArray(row.conditions)
          ? row.conditions
          : [];
        const nextConditions = baseConditions.filter(
          (_, idx) => idx !== conditionIndex,
        );

        const nextRow = {
          ...row,
          conditions: nextConditions,
        };

        if (row.filterMode === "builder") {
          nextRow.filterJson = JSON.stringify(
            buildFilterFromConditions(nextRow),
            null,
            2,
          );
        }

        return nextRow;
      }),
    );
  };

  const updateCombinator = (rowIndex, combinator) => {
    if (!combinator) return;

    setRows((previous) =>
      previous.map((row, index) => {
        if (index !== rowIndex) return row;

        const nextRow = {
          ...row,
          combinator,
        };

        if (row.filterMode === "builder") {
          nextRow.filterJson = JSON.stringify(
            buildFilterFromConditions(nextRow),
            null,
            2,
          );
        }

        return nextRow;
      }),
    );
  };

  const updateFilterMode = (rowIndex, mode) => {
    if (!mode) return;

    setRows((previous) =>
      previous.map((row, index) => {
        if (index !== rowIndex) return row;

        const nextRow = {
          ...row,
          filterMode: mode,
        };

        if (mode === "builder") {
          nextRow.filterJson = JSON.stringify(
            buildFilterFromConditions(nextRow),
            null,
            2,
          );
        }

        return nextRow;
      }),
    );
  };

  const removeRow = (index) => {
    setRows((previous) => previous.filter((_, rowIndex) => rowIndex !== index));
  };

  const parseRows = () => {
    const parsedRows = [];
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      const entityName = row.entityName.trim();
      if (!entityName) {
        return {
          error: `Entity name is required on row ${index + 1}.`,
          data: null,
        };
      }

      let parsedFilter = {};
      const candidateFilter =
        row.filterMode === "builder"
          ? JSON.stringify(buildFilterFromConditions(row))
          : (row.filterJson || "").trim();
      if (candidateFilter) {
        try {
          parsedFilter = JSON.parse(candidateFilter);
        } catch (err) {
          return {
            error: `Filter JSON is invalid on row ${index + 1}.`,
            data: null,
          };
        }

        if (
          typeof parsedFilter !== "object" ||
          parsedFilter === null ||
          Array.isArray(parsedFilter)
        ) {
          return {
            error: `Filter JSON must be an object on row ${index + 1}.`,
            data: null,
          };
        }
      }

      parsedRows.push({
        entityName,
        filter: parsedFilter,
      });
    }

    return { error: null, data: parsedRows };
  };

  const handleQueueArtifact = async () => {
    const parsed = parseRows();
    if (parsed.error) {
      showMessage(parsed.error, "error");
      return;
    }

    setSubmitting(true);

    const response = await addToWorkQueue("createArtifactPackage", {
      artifactName: artifactName.trim(),
      description: description.trim(),
      exportMode,
      entities: parsed.data,
      requestedAt: new Date().toISOString(),
      requestedFrom: "ArtifactBuilder",
    });

    setSubmitting(false);

    if (response?.isSuccessful) {
      showMessage("Artifact creation has been queued.", "success");
      return;
    }

    showMessage(response?.message || "Unable to queue artifact job.", "error");
  };

  const resetImportState = () => {
    setImportSourceUrl("");
    setImportFileName("");
    setImportPayload(null);
  };

  const handleOpenImportDialog = () => {
    if (!canImportArtifact) {
      showMessage(
        artifactImportPolicy?.reason ||
          "Artifact import is currently unavailable.",
        "error",
      );
      return;
    }

    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    if (importing) return;
    setImportDialogOpen(false);
    resetImportState();
  };

  const handleArtifactFileSelected = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (Number(file.size || 0) > MAX_INLINE_ARTIFACT_BYTES) {
      setImportPayload(null);
      setImportFileName("");
      showMessage(
        "Artifact file is too large for inline upload. Use Artifact URL import for large packages.",
        "error",
      );
      return;
    }

    try {
      const rawText = await file.text();
      if (
        new TextEncoder().encode(rawText).length > MAX_INLINE_ARTIFACT_BYTES
      ) {
        setImportPayload(null);
        setImportFileName("");
        showMessage(
          "Artifact file is too large for inline upload. Use Artifact URL import for large packages.",
          "error",
        );
        return;
      }

      const parsed = JSON.parse(rawText);

      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        showMessage("Artifact file must contain a JSON object.", "error");
        return;
      }

      if (!Array.isArray(parsed.entities)) {
        showMessage("Artifact file is missing an entities array.", "error");
        return;
      }

      setImportPayload(parsed);
      setImportFileName(file.name || "artifact.json");
    } catch (err) {
      showMessage("Artifact file is not valid JSON.", "error");
    }
  };

  const handleQueueImport = async () => {
    if (!canImportArtifact) {
      showMessage(
        artifactImportPolicy?.reason ||
          "Artifact import is currently unavailable.",
        "error",
      );
      return;
    }

    const sourceUrl = importSourceUrl.trim();
    if (!sourceUrl && !importPayload) {
      showMessage(
        "Provide an artifact URL or upload an artifact JSON file.",
        "error",
      );
      return;
    }

    if (!sourceUrl && importPayload) {
      try {
        const payloadBytes = new TextEncoder().encode(
          JSON.stringify(importPayload),
        ).length;
        if (payloadBytes > MAX_INLINE_ARTIFACT_BYTES) {
          showMessage(
            "Artifact JSON is too large for inline import. Use Artifact URL import for large packages.",
            "error",
          );
          return;
        }
      } catch (err) {
        showMessage("Artifact payload could not be serialized.", "error");
        return;
      }
    }

    setImporting(true);

    const response = await addToWorkQueue("importArtifactPackage", {
      requestedAt: new Date().toISOString(),
      requestedFrom: "ArtifactBuilder",
      dryRun: importDryRun,
      artifactSourceUrl: sourceUrl || undefined,
      artifactPayload: importPayload || undefined,
    });

    setImporting(false);

    if (response?.isSuccessful) {
      showMessage("Artifact import has been queued.", "success");
      setImportDialogOpen(false);
      resetImportState();
      return;
    }

    showMessage(
      response?.message || "Unable to queue artifact import.",
      "error",
    );
  };

  const handleToolbarClick = (item) => {
    if (item === "home") {
      navigate("/home");
      return;
    }

    if (item === "import") {
      handleOpenImportDialog();
    }
  };

  return (
    <Page
      pageName="Artifact Builder"
      includeToolbar
      items={toolbarItems}
      onToolItemClick={handleToolbarClick}
    >
      <Card title="Create Artifact Package">
        <Stack
          spacing={2}
          sx={{ p: 2 }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
          >
            ENVIRONMENT: {environment}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            Use the toolbar Import action to queue artifact imports (upsert
            behavior).
          </Typography>

          {artifactPolicyLoaded && !canImportArtifact && (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              Artifact import unavailable: {artifactImportPolicy?.reason}
            </Typography>
          )}

          <TextField
            label="Artifact Name"
            value={artifactName}
            onChange={(event) => setArtifactName(event.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          <TextField
            select
            label="Export Mode"
            value={exportMode}
            onChange={(event) => setExportMode(event.target.value)}
            size="small"
            sx={{ maxWidth: 320 }}
          >
            <MenuItem value="mirror-config">
              Mirror Config (include project children + SKU config)
            </MenuItem>
            <MenuItem value="standard">Standard (selected rows only)</MenuItem>
          </TextField>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            Mirror Config auto-includes project descendants and SKU
            configuration for replication.
          </Typography>

          <Divider />

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2">Entities and Filters</Typography>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={addRow}
            >
              Add Entity
            </Button>
          </Box>

          <Stack spacing={2}>
            {rows.map((row, index) => (
              <Box
                key={`artifact-row-${index}`}
                sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1.5 }}
              >
                <Stack spacing={1.5}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Row {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeRow(index)}
                      disabled={rows.length === 1}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <TextField
                    select
                    label="Filter Mode"
                    value={row.filterMode || "builder"}
                    onChange={(event) =>
                      updateFilterMode(index, event.target.value)
                    }
                    size="small"
                    sx={{ width: 220 }}
                  >
                    <MenuItem value="builder">Visual Builder</MenuItem>
                    <MenuItem value="advanced">Advanced JSON</MenuItem>
                  </TextField>

                  <Autocomplete
                    freeSolo
                    options={ENTITY_OPTIONS}
                    value={row.entityName || ""}
                    onInputChange={(_, value) =>
                      updateRow(index, "entityName", value)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Entity Name"
                        size="small"
                        fullWidth
                        helperText="Choose a common entity or type a custom one"
                      />
                    )}
                  />

                  {row.filterMode === "builder" ? (
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                      >
                        <TextField
                          label="Preset Name"
                          size="small"
                          value={row.presetName || ""}
                          onChange={(event) =>
                            updateRow(index, "presetName", event.target.value)
                          }
                          sx={{ minWidth: 220 }}
                        />

                        <Button onClick={() => savePreset(index)}>
                          Save Preset
                        </Button>

                        <TextField
                          select
                          size="small"
                          label="Load Preset"
                          value={row.selectedPresetId || ""}
                          onChange={(event) =>
                            updateRow(
                              index,
                              "selectedPresetId",
                              event.target.value,
                            )
                          }
                          sx={{ minWidth: 260 }}
                        >
                          <MenuItem value="">Select preset...</MenuItem>
                          {presets.map((preset) => (
                            <MenuItem
                              key={preset.id}
                              value={preset.id}
                            >
                              {preset.name}
                            </MenuItem>
                          ))}
                        </TextField>

                        <Button onClick={() => applyPreset(index)}>
                          Apply Preset
                        </Button>
                      </Stack>

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Filter Conditions
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => addCondition(index)}
                        >
                          Add Condition
                        </Button>
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Leave conditions empty to extract the entire table.
                      </Typography>

                      <ToggleButtonGroup
                        size="small"
                        value={row.combinator || "and"}
                        exclusive
                        onChange={(_, value) => updateCombinator(index, value)}
                      >
                        <ToggleButton value="and">AND</ToggleButton>
                        <ToggleButton value="or">OR</ToggleButton>
                      </ToggleButtonGroup>

                      {(Array.isArray(row.conditions)
                        ? row.conditions
                        : []
                      ).map((condition, conditionIndex) => (
                        <Box
                          key={`row-${index}-condition-${conditionIndex}`}
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-start",
                            gap: 1,
                            overflowX: "auto",
                            pb: 0.5,
                          }}
                        >
                          <Autocomplete
                            freeSolo
                            options={fieldPathOptions}
                            value={condition.field || ""}
                            onInputChange={(_, value) =>
                              updateCondition(
                                index,
                                conditionIndex,
                                "field",
                                value,
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Field Path"
                                size="small"
                                fullWidth
                                placeholder="status"
                                sx={{ minWidth: 220 }}
                              />
                            )}
                            sx={{ minWidth: 220, flex: 1 }}
                          />

                          <TextField
                            select
                            label="Operator"
                            value={condition.operator || "equals"}
                            onChange={(event) =>
                              updateCondition(
                                index,
                                conditionIndex,
                                "operator",
                                event.target.value,
                              )
                            }
                            size="small"
                            sx={{ minWidth: 210 }}
                          >
                            {OPERATOR_OPTIONS.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            select
                            label="Type"
                            value={condition.valueType || "string"}
                            onChange={(event) =>
                              updateCondition(
                                index,
                                conditionIndex,
                                "valueType",
                                event.target.value,
                              )
                            }
                            size="small"
                            sx={{ minWidth: 170 }}
                          >
                            {VALUE_TYPE_OPTIONS.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            label={
                              condition.operator === "exists"
                                ? "Exists? (true/false)"
                                : "Value"
                            }
                            value={condition.value || ""}
                            onChange={(event) =>
                              updateCondition(
                                index,
                                conditionIndex,
                                "value",
                                event.target.value,
                              )
                            }
                            size="small"
                            sx={{ minWidth: 260, flex: 1 }}
                            error={Boolean(validateConditionValue(condition))}
                            helperText={
                              validateConditionValue(condition) ||
                              conditionHint(condition)
                            }
                          />

                          <Button
                            color="error"
                            onClick={() =>
                              removeCondition(index, conditionIndex)
                            }
                          >
                            Remove
                          </Button>
                        </Box>
                      ))}

                      {(Array.isArray(row.conditions)
                        ? row.conditions.length
                        : 0) === 0 ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          No conditions configured. Filter resolves to {}
                          (all rows).
                        </Typography>
                      ) : null}

                      <TextField
                        label="Generated Filter JSON"
                        value={JSON.stringify(
                          buildFilterFromConditions(row),
                          null,
                          2,
                        )}
                        size="small"
                        fullWidth
                        multiline
                        minRows={4}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Stack>
                  ) : (
                    <TextField
                      label="Filter JSON"
                      value={row.filterJson}
                      onChange={(event) =>
                        updateRow(index, "filterJson", event.target.value)
                      }
                      size="small"
                      fullWidth
                      multiline
                      minRows={4}
                      placeholder='{"status":"active"}'
                    />
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>

          <Box
            display="flex"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              onClick={handleQueueArtifact}
              disabled={!canSubmit}
            >
              {submitting ? "Queueing..." : "Queue Artifact Build"}
            </Button>
          </Box>
        </Stack>
      </Card>

      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Import Artifact Package</DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            sx={{ pt: 1 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Provide either an artifact URL or upload an artifact JSON file.
            </Typography>

            <TextField
              label="Artifact URL"
              value={importSourceUrl}
              onChange={(event) => setImportSourceUrl(event.target.value)}
              size="small"
              fullWidth
              placeholder="https://bucket.endpoint/artifact-exports/example.json"
            />

            <Button
              variant="outlined"
              component="label"
            >
              Upload Artifact JSON
              <input
                type="file"
                accept="application/json,.json"
                hidden
                onChange={handleArtifactFileSelected}
              />
            </Button>

            {importFileName ? (
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Loaded file: {importFileName}
              </Typography>
            ) : null}

            <TextField
              select
              label="Import Mode"
              value={importDryRun ? "dry-run" : "apply"}
              onChange={(event) =>
                setImportDryRun(event.target.value === "dry-run")
              }
              size="small"
              sx={{ maxWidth: 280 }}
            >
              <MenuItem value="dry-run">Dry Run (preview only)</MenuItem>
              <MenuItem value="apply">Apply Changes</MenuItem>
            </TextField>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              Import performs upserts and, for mirror-config artifacts, scoped
              deletes to match source. Use Dry Run first.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseImportDialog}
            disabled={importing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleQueueImport}
            disabled={importing}
          >
            {importing ? "Queueing..." : "Queue Import"}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default ArtifactBuilder;
